import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Scale, ArrowLeft, ArrowRight, Loader2, Sparkles, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

/**
 * Sprint K · Legal Analysis · New (template picker → jurisdiction / practice
 * area / document selection → confirm).
 *
 * Route: /app/legal/analysis/new[?template=<slug>]
 *
 * On confirm we:
 *   1. POST /orchestrations {template_slug} → creates the tenant-scoped clone
 *   2. POST /orchestrations/{id}/run {trigger_payload:{jurisdiction,practice_area,document_id?,document_text?}}
 *   3. Route to /app/legal/analysis/{orchId}/runs/{runId}
 */
export default function LegalAnalysisNew() {
    const nav = useNavigate();
    const [params] = useSearchParams();
    const { user } = useAuth();
    const isOwner = user?.role === "platform_owner";

    const [templates, setTemplates] = useState([]);
    const [jurisdictions, setJurisdictions] = useState([]);
    const [practiceAreas, setPracticeAreas] = useState([]);
    const [matters, setMatters] = useState([]);
    const [matterDocs, setMatterDocs] = useState([]);
    const [step, setStep] = useState(0);
    const [busy, setBusy] = useState(false);

    const [form, setForm] = useState({
        template_slug: params.get("template") || "",
        jurisdiction: "",
        practice_area: "",
        matter_id: "",
        document_id: "",
        document_text: "",
        additional_facts: "",
    });

    const currentTemplate = useMemo(
        () => templates.find((t) => t.slug === form.template_slug),
        [templates, form.template_slug],
    );

    useEffect(() => {
        (async () => {
            try {
                const [tpl, j, p, m] = await Promise.all([
                    api.get("/orchestrations/templates"),
                    api.get("/legal/jurisdictions"),
                    api.get("/legal/practice-areas"),
                    api.get("/legal/matters?limit=200").catch(() => ({ data: { items: [] } })),
                ]);
                setTemplates((tpl.data.templates || []).filter((t) => t.is_legal));
                setJurisdictions(j.data.jurisdictions || []);
                setPracticeAreas(p.data.practice_areas || []);
                setMatters(m.data.items || []);
            } catch (err) {
                toast.error("Could not load reference data.");
            }
        })();
    }, []);

    useEffect(() => {
        if (!form.matter_id) {
            setMatterDocs([]);
            return;
        }
        (async () => {
            try {
                const r = await api.get(`/legal/matters/${form.matter_id}/documents`);
                setMatterDocs(r.data?.documents || r.data || []);
            } catch {
                setMatterDocs([]);
            }
        })();
    }, [form.matter_id]);

    const canNext = () => {
        if (step === 0) return !!form.template_slug;
        if (step === 1) return !!form.jurisdiction && !!form.practice_area;
        if (step === 2) return !!form.document_id || !!form.document_text.trim();
        return true;
    };

    const submit = async () => {
        setBusy(true);
        try {
            const created = await api.post("/orchestrations", {
                name: currentTemplate?.name || "Legal analysis",
                template_slug: form.template_slug,
            });
            const orchId = created.data?.orchestration?.id;
            if (!orchId) throw new Error("Could not create orchestration.");

            const triggerPayload = {
                jurisdiction: form.jurisdiction,
                practice_area: form.practice_area,
            };
            if (form.document_id) triggerPayload.document_id = form.document_id;
            if (form.document_text.trim()) triggerPayload.document_text = form.document_text.trim();
            if (form.additional_facts.trim()) {
                triggerPayload.facts = form.additional_facts.trim();
                triggerPayload.pleading_text = form.additional_facts.trim();
                triggerPayload.policy_text = form.additional_facts.trim();
                triggerPayload.statute_citation = form.additional_facts.trim();
            }

            const run = await api.post(`/orchestrations/${orchId}/run`, {
                trigger_payload: triggerPayload,
            });
            const runId = run.data?.run_id;
            nav(`/app/legal/analysis/${orchId}/runs/${runId}`);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 402) {
                // PaywallModal already dispatched — leave user here.
                return;
            }
            const msg = err?.response?.data?.detail?.message || err?.response?.data?.detail || err?.message;
            toast.error(typeof msg === "string" ? msg : "Could not start analysis.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="h-full overflow-y-auto wfos-scrollbar" data-testid="legal-analysis-new">
            <div className="max-w-3xl mx-auto p-8">
                <button
                    onClick={() => nav(-1)}
                    className="flex items-center gap-1.5 text-sm mb-6"
                    style={{ color: "var(--atlas-text-tertiary)" }}
                >
                    <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
                    Back
                </button>

                <div className="mb-6">
                    <div className="wfos-label" style={{ color: "var(--atlas-text-tertiary)" }}>
                        Legal Analysis · New
                    </div>
                    <h1 className="text-2xl font-semibold mt-1" style={{ color: "var(--atlas-text-primary)" }}>
                        Start a legal analysis
                    </h1>
                    <div className="mt-3 flex items-center gap-2">
                        {["Template", "Jurisdiction", "Documents", "Review"].map((label, i) => (
                            <React.Fragment key={label}>
                                <div
                                    className="px-2.5 py-1 rounded-full text-[10px]"
                                    style={{
                                        background: i <= step ? "rgba(139,92,246,0.20)" : "var(--atlas-bg-deep)",
                                        border: `1px solid ${i <= step ? "var(--atlas-primary)" : "var(--atlas-border-soft)"}`,
                                        color: i <= step ? "var(--atlas-primary-hover)" : "var(--atlas-text-tertiary)",
                                    }}
                                >
                                    {i + 1}. {label}
                                </div>
                                {i < 3 && (
                                    <div className="w-6 h-px" style={{ background: "var(--atlas-border-soft)" }} />
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                <div
                    className="p-6 rounded-xl"
                    style={{
                        background: "var(--atlas-bg-elevated)",
                        border: "1px solid var(--atlas-border-strong)",
                    }}
                >
                    {step === 0 && (
                        <div className="space-y-3">
                            <div className="wfos-label" style={{ color: "var(--atlas-text-tertiary)" }}>
                                Select a template
                            </div>
                            {templates.map((t) => {
                                const on = form.template_slug === t.slug;
                                return (
                                    <button
                                        key={t.slug}
                                        type="button"
                                        onClick={() => setForm({ ...form, template_slug: t.slug })}
                                        className="w-full text-left p-4 rounded-lg transition"
                                        style={{
                                            background: on ? "rgba(139,92,246,0.10)" : "var(--atlas-bg-deep)",
                                            border: `1px solid ${on ? "var(--atlas-primary)" : "var(--atlas-border-soft)"}`,
                                        }}
                                        data-testid={`legal-new-template-${t.slug}`}
                                    >
                                        <div className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--atlas-text-primary)" }}>
                                            <span>{t.icon}</span>
                                            {t.name}
                                        </div>
                                        <div className="mt-1 text-xs" style={{ color: "var(--atlas-text-secondary)" }}>
                                            {t.description}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {step === 1 && (
                        <div className="space-y-5">
                            <div>
                                <label className="wfos-label mb-2 block" style={{ color: "var(--atlas-text-tertiary)" }}>
                                    Jurisdiction
                                </label>
                                <select
                                    value={form.jurisdiction}
                                    onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg text-sm"
                                    style={{
                                        background: "var(--atlas-bg-deep)",
                                        border: "1px solid var(--atlas-border-soft)",
                                        color: "var(--atlas-text-primary)",
                                    }}
                                    data-testid="legal-new-jurisdiction"
                                >
                                    <option value="">Select jurisdiction…</option>
                                    {jurisdictions.map((j) => (
                                        <option key={j.code} value={j.code}>{j.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="wfos-label mb-2 block" style={{ color: "var(--atlas-text-tertiary)" }}>
                                    Practice area
                                </label>
                                <div className="flex flex-wrap gap-2" data-testid="legal-new-practice-area">
                                    {practiceAreas.map((p) => {
                                        const on = form.practice_area === p.code;
                                        return (
                                            <button
                                                key={p.code}
                                                type="button"
                                                onClick={() => setForm({ ...form, practice_area: p.code })}
                                                className="px-3 py-1.5 rounded-full text-xs transition"
                                                style={{
                                                    background: on ? "rgba(139,92,246,0.20)" : "var(--atlas-bg-deep)",
                                                    border: `1px solid ${on ? "var(--atlas-primary)" : "var(--atlas-border-soft)"}`,
                                                    color: on ? "var(--atlas-primary-hover)" : "var(--atlas-text-secondary)",
                                                }}
                                                data-testid={`legal-new-pa-${p.code}`}
                                            >
                                                {p.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-5">
                            <div>
                                <label className="wfos-label mb-2 block" style={{ color: "var(--atlas-text-tertiary)" }}>
                                    Matter (optional — pull documents from a matter)
                                </label>
                                <select
                                    value={form.matter_id}
                                    onChange={(e) => setForm({ ...form, matter_id: e.target.value, document_id: "" })}
                                    className="w-full px-3 py-2 rounded-lg text-sm"
                                    style={{
                                        background: "var(--atlas-bg-deep)",
                                        border: "1px solid var(--atlas-border-soft)",
                                        color: "var(--atlas-text-primary)",
                                    }}
                                    data-testid="legal-new-matter"
                                >
                                    <option value="">No matter — paste facts below</option>
                                    {matters.map((m) => (
                                        <option key={m.id} value={m.id}>{m.title}</option>
                                    ))}
                                </select>
                            </div>
                            {form.matter_id && matterDocs.length > 0 && (
                                <div>
                                    <label className="wfos-label mb-2 block" style={{ color: "var(--atlas-text-tertiary)" }}>
                                        Document
                                    </label>
                                    <select
                                        value={form.document_id}
                                        onChange={(e) => setForm({ ...form, document_id: e.target.value })}
                                        className="w-full px-3 py-2 rounded-lg text-sm"
                                        style={{
                                            background: "var(--atlas-bg-deep)",
                                            border: "1px solid var(--atlas-border-soft)",
                                            color: "var(--atlas-text-primary)",
                                        }}
                                        data-testid="legal-new-document"
                                    >
                                        <option value="">Select a document…</option>
                                        {matterDocs.map((d) => (
                                            <option key={d.id} value={d.id}>{d.title}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div>
                                <label className="wfos-label mb-2 block" style={{ color: "var(--atlas-text-tertiary)" }}>
                                    Or paste facts / statute citation / pleading text
                                </label>
                                <textarea
                                    rows={6}
                                    value={form.document_text}
                                    onChange={(e) => setForm({ ...form, document_text: e.target.value })}
                                    placeholder="Paste the contract, pleading, statute, or facts here…"
                                    className="w-full px-3 py-2 rounded-lg text-sm"
                                    style={{
                                        background: "var(--atlas-bg-deep)",
                                        border: "1px solid var(--atlas-border-soft)",
                                        color: "var(--atlas-text-primary)",
                                    }}
                                    data-testid="legal-new-document-text"
                                />
                            </div>
                            <div>
                                <label className="wfos-label mb-2 block" style={{ color: "var(--atlas-text-tertiary)" }}>
                                    Additional facts (optional)
                                </label>
                                <textarea
                                    rows={3}
                                    value={form.additional_facts}
                                    onChange={(e) => setForm({ ...form, additional_facts: e.target.value })}
                                    placeholder="Any additional context the analysis should consider…"
                                    className="w-full px-3 py-2 rounded-lg text-sm"
                                    style={{
                                        background: "var(--atlas-bg-deep)",
                                        border: "1px solid var(--atlas-border-soft)",
                                        color: "var(--atlas-text-primary)",
                                    }}
                                    data-testid="legal-new-facts"
                                />
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-4 text-sm" style={{ color: "var(--atlas-text-secondary)" }}>
                            <div>
                                <span className="wfos-label" style={{ color: "var(--atlas-text-tertiary)" }}>Template · </span>
                                {currentTemplate?.name}
                            </div>
                            <div>
                                <span className="wfos-label" style={{ color: "var(--atlas-text-tertiary)" }}>Jurisdiction · </span>
                                {jurisdictions.find((j) => j.code === form.jurisdiction)?.label}
                            </div>
                            <div>
                                <span className="wfos-label" style={{ color: "var(--atlas-text-tertiary)" }}>Practice area · </span>
                                {practiceAreas.find((p) => p.code === form.practice_area)?.label}
                            </div>
                            <div>
                                <span className="wfos-label" style={{ color: "var(--atlas-text-tertiary)" }}>Documents · </span>
                                {form.document_id ? "Matter document" : `${form.document_text.length} chars pasted`}
                            </div>
                            <div
                                className="p-3 rounded-lg text-xs"
                                style={{
                                    background: "rgba(245,158,11,0.10)",
                                    border: "1px solid rgba(245,158,11,0.35)",
                                    color: "#fbbf24",
                                }}
                            >
                                Every output is AI-generated legal information — not legal advice.
                                Attorney of record must acknowledge before delivery.
                            </div>
                        </div>
                    )}

                    <div className="mt-6 flex items-center justify-between">
                        <button
                            type="button"
                            onClick={() => setStep((s) => Math.max(0, s - 1))}
                            disabled={step === 0}
                            className="px-4 py-2 rounded-lg text-sm disabled:opacity-40"
                            style={{
                                border: "1px solid var(--atlas-border-strong)",
                                color: "var(--atlas-text-tertiary)",
                                background: "transparent",
                            }}
                            data-testid="legal-new-back"
                        >
                            Back
                        </button>
                        {step < 3 && (
                            <button
                                type="button"
                                disabled={!canNext()}
                                onClick={() => setStep((s) => s + 1)}
                                className="px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-40"
                                style={{
                                    background: "var(--atlas-primary)",
                                    color: "white",
                                }}
                                data-testid="legal-new-next"
                            >
                                <span className="inline-flex items-center gap-1.5">
                                    Next
                                    <ArrowRight className="w-4 h-4" strokeWidth={2} />
                                </span>
                            </button>
                        )}
                        {step === 3 && (
                            <button
                                type="button"
                                disabled={busy}
                                onClick={submit}
                                className="px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-40"
                                style={{
                                    background: "var(--atlas-primary)",
                                    color: "white",
                                }}
                                data-testid="legal-new-submit"
                            >
                                <span className="inline-flex items-center gap-1.5">
                                    {busy ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Starting analysis…
                                        </>
                                    ) : (
                                        <>
                                            <Sparkles className="w-4 h-4" strokeWidth={2} />
                                            Start analysis
                                        </>
                                    )}
                                </span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
